/**
 * CheckoutStepBilling Component
 */

import { consumeLocallyStoredStep } from '../../../redux/actions/checkout';
import { setUserStripeToken } from '../../../redux/actions/user';
import { validateFields } from '../../../redux/form/validation';
import { CheckoutStepFormName, CheckoutStepId, DefaultBillingAddressIsSameAsLicensee } from './config';

import { extend, get } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { injectStripe, ReactStripeElements } from 'react-stripe-elements';
import { Dispatch } from 'redux';
import { Field, FormProps, getFormValues, reduxForm, SubmissionError } from 'redux-form';

import { getBillingAddress } from '../../../redux/form/selectors';
import { getCountryLabelFromCode, getStateLabelFromCode } from '../../../redux/helpers/countries';
// import { logComponentUpdate } from '../../../utils/log';
import Heading from '../../atoms/text/Heading';
import CheckboxWithLabel from '../../molecules/forms/CheckboxWithLabel';
import FormCell from '../../molecules/forms/FormCell';
import FormRow from '../../molecules/forms/FormRow';
import Select from '../../molecules/forms/Select';
import StripeCardElement from '../../molecules/forms/StripeCardElement';
import TextField from '../../molecules/forms/TextFieldWithLabel';
import { renderChequePaymentInformation } from '../../organisms/account/Invoice/InvoicePaymentMethods';
import AddressForm, { getAddressFormValidations } from '../../organisms/forms/AddressForm';
import CheckoutStep from './CheckoutStep';

const formValidations: IFormValidations = getAddressFormValidations();

interface IOwnStateProps {
  address: IAddress;
  sameAddressAsLicensee: boolean;
  paymentMethod: GQL.OrderPaymentMethod;
  initialValues: any;
  initialValuesFromLocalStorage: boolean;
  isActive: boolean;
  businessInformation: GQL.IBusinessInformation;
  getCountryLabel(countryCode: string): string | undefined;
  getStateLabel(stateCode: string): string | undefined;
}

interface IOwnProps {
  stripe: ReactStripeElements.StripeProps;
}

interface IOwnDispatchProps {
  consumeLocalStorage: () => void;
}

type CheckoutStepBillingProps = IOwnProps & IOwnStateProps & IOwnDispatchProps & FormProps<any, any, {}>;

interface IFormFields {
  address: IAddress;
  nameOnCard: string;
  postalCodeValidationRegExp: string;
  sameAddressAsLicensee: boolean;
  paymentMethod: GQL.OrderPaymentMethod;
}

export type CheckoutStepBillingFormFields = IFormFields;

interface IOwnState {
  stripeError?: string;
}

const stepId = CheckoutStepId.billing;
const form = CheckoutStepFormName.billing;
const classes = new BEM(`checkout__step--${stepId}`);

const paymentMethodOptions: ISelectStoreItem[] = [
  { label: 'Credit Card', value: GQL.OrderPaymentMethod.Stripe },
  { label: 'Cheque', value: GQL.OrderPaymentMethod.Cheque },
  // { label: 'Wire Transfer', value: GQL.OrderPaymentMethod.Wire },
];

class CheckoutStepBilling extends React.Component<CheckoutStepBillingProps, IOwnState> {

  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    this.props.consumeLocalStorage();
  }

  shouldComponentUpdate(nextProps: CheckoutStepBillingProps) {
    // return nextProps.isActive || !isEqual(this.props.address, nextProps.address);
    return nextProps.isActive;
  }

  componentDidUpdate(prevProps: CheckoutStepBillingProps) {
    // logComponentUpdate('Checkout Billing:', this.props, prevProps);
  }

  validate = (values: IFormFields, dispatch: Dispatch<any>) => {
    const {
      sameAddressAsLicensee,
      paymentMethod,
    } = this.props;

    const promises: Array<Promise<any>> = [];

    if (paymentMethod === GQL.OrderPaymentMethod.Stripe) {
      promises.push(this.createStripeToken(values, dispatch));
    }

    if (!sameAddressAsLicensee) {
      promises.push(validateFields(values, formValidations, {
        validatePostalCode: [values.hasOwnProperty('postalCodeValidationRegExp') && values.postalCodeValidationRegExp],
      }));
    }

    return Promise.all(promises);
  }

  render() {
    const {
      error,
      handleSubmit,
      paymentMethod,
      sameAddressAsLicensee,
      submitting,
    } = this.props;

    return (
      <CheckoutStep
        customValidation={this.validate}
        error={error}
        id={stepId}
        handleSubmit={handleSubmit!}
        submitting={submitting}
        title="Billing Information"
      // defaultSelected
      >
        <div>
          <Heading is="h5">Billing Address</Heading>
          <Field
            component={CheckboxWithLabel}
            className="checkbox-same-address"
            name="sameAddressAsLicensee"
            label="Same as Licensee Address"
          />
          <div {...classes('form') }>
            {!sameAddressAsLicensee && (
              <AddressForm form={form} {...classes('address-form') } />
            )}
            <FormRow>
              <FormCell minimumWidth={8}>
                <Select
                  form={form}
                  label="Payment Method"
                  name="paymentMethod"
                  options={paymentMethodOptions}
                  selectName="payment-method"
                  selectProps={{
                    autoBlur: true,
                    clearable: false,
                  }}
                />
              </FormCell>
            </FormRow>
            {paymentMethod === GQL.OrderPaymentMethod.Stripe ? this.renderStripeElement() : ''}
            {paymentMethod === GQL.OrderPaymentMethod.Cheque ?
              this.renderChequeElement() : ''}
          </div>
        </div>
      </CheckoutStep>
    );
  }

  private renderChequeElement() {
    const {
      businessInformation: {
        name,
        legal_name: legalName,
        address,
      },
      getCountryLabel,
      getStateLabel,
    } = this.props;

    return (
      <FormRow>
        <FormCell>
          {renderChequePaymentInformation({
            address, legalName, name, getCountryLabel, getStateLabel, includeHeading: false,
          })}
        </FormCell>
      </FormRow>
    );
  }

  private renderStripeElement() {
    const {
      address,
    } = this.props;

    const postalCode = address ? address.postalCode : '';

    const {
      stripeError,
    } = this.state;

    return (
      <>
        <FormRow>
              <FormCell minimumWidth={10}>
                <Field
                  name="nameOnCard"
                  component={TextField}
                  label="Cardholder Name"
                  colorScheme="light"
                />
              </FormCell>
            </FormRow>
            <FormRow>
              <FormCell minimumWidth={10}>
                <StripeCardElement
                  error={stripeError}
                  label="Credit or Debit Card"
                  value={{ postalCode }}
                />
              </FormCell>
            </FormRow>
      </>
    );
  }

  private createStripeToken(values: IFormFields, dispatch: Dispatch<any>) {
    const {
      address,
      stripe,
    } = this.props;

    return new Promise((resolve, reject) => {

      const cardData = {
        name: values.nameOnCard,
        address_line1: address.street,
        address_line2: address.unit,
        address_city: address.city,
        address_state: address.state,
        address_country: address.country,
      };

      stripe.createToken(cardData).then(({ error, token }: stripe.TokenResponse) => {
        if (error) {
          this.setState({ stripeError: error.message }, () => {
            reject(new SubmissionError());
          });
        } else {
          const country = get(this.props, 'address.country', '');
          const postalCode = get(this.props, 'address.postalCode', '');
          if (country && postalCode) {
            if (token!.card!.country !== country) {
              return this.setState({
                stripeError: `Card's country of origin does not match the billing address.`,
              }, () => {
                reject(new SubmissionError());
              });
            }
          }
          dispatch(setUserStripeToken({ token: token! }));
          resolve();
        }
      });
    });
  }
}

const CheckoutStepBillingForm = reduxForm<IFormFields, any, any>({
  forceUnregisterOnUnmount: true,
  form,
})(CheckoutStepBilling);

const ConnectedCheckoutStepBillingForm =
  connect<IOwnStateProps, IOwnDispatchProps, IOwnProps, CheckoutStepBillingProps, IStoreState>(
  (state) => {
    const formState = getFormValues<any, IStoreState>(form)(state);
    const localStorage = get(state.checkout.localStorage, stepId);
    const initialValuesFromLocalStorage = !!localStorage;

    let initialValues: Partial<IFormFields> = {
      sameAddressAsLicensee: DefaultBillingAddressIsSameAsLicensee,
      paymentMethod: GQL.OrderPaymentMethod.Stripe,
    };

    if (localStorage) {
      initialValues = extend(initialValues, localStorage) as IFormFields;
    }

    let sameAddressAsLicensee = initialValues.sameAddressAsLicensee!;

    if (formState) {
      sameAddressAsLicensee = get(formState, 'sameAddressAsLicensee', sameAddressAsLicensee);
    }

    return {
      address: getBillingAddress(state),
      sameAddressAsLicensee,
      paymentMethod: get(formState, 'paymentMethod'),
      businessInformation: state.staticData.businessInformation as GQL.IBusinessInformation,
      getCountryLabel: (countryCode) => getCountryLabelFromCode(countryCode)(state),
      getStateLabel: (stateCode) => getStateLabelFromCode(stateCode)(state),
      initialValues,
      initialValuesFromLocalStorage,
      isActive: state.checkout.activeStep === stepId,
    };
  },
  (dispatch) => ({
    consumeLocalStorage: () => dispatch(consumeLocallyStoredStep({ id: stepId })),
  }),
  (stateProps, dispatchProps, ownProps) => ({
    ...ownProps,
    ...stateProps,
    consumeLocalStorage: () => stateProps.initialValuesFromLocalStorage && dispatchProps.consumeLocalStorage(),
  }),
)(CheckoutStepBillingForm);

const WithStripe = injectStripe(ConnectedCheckoutStepBillingForm);

export default WithStripe as React.ComponentClass<Pick<CheckoutStepBillingProps, never>>;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/Checkout/CheckoutStepBilling.tsx